import { ILocale as Locale } from '@aurorasolar/intl';
import { LDSingleKindContext } from 'launchdarkly-react-client-sdk';
import isEqual from 'lodash.isequal';
import { z } from 'zod';

import { Experiment, LDFlagsDashCase } from '@/consts/LDFlags';
import { Channel } from '@/helpers/attribution';
import { stripNulls } from '@/helpers/formatters';

import { Address, geolocationSchema } from './Address';
import { AffiliateId } from './AffiliateId';
import { ContactMessage } from './ContactMessage';
import { FunnelType } from './FunnelType';
import { PaymentType } from './Payment';

export type Visitor = {
  _id?: string; // use vid instead as it is more consistent
  address?: Address;
  alternate?: string;
  battery_option?: BatteryChoice;
  bill?: number;
  data_found?: boolean;
  email?: string;
  email_verified?: boolean;
  email_verify_check?: string;
  first_name?: string;
  geolocation?: Partial<Address>;
  in_market?: boolean;
  intent?: string;
  isBot?: boolean;
  last_name?: string;
  locale?: Locale;
  message?: ContactMessage;
  meta_values: MetaValues;
  notify_on_installers_available?: boolean;
  otp_skipped_at?: string;
  otp_verify_check?: boolean;
  panel_option?: PanelChoice;
  partner_ids?: string[];
  payment_type?: PaymentType;
  phone?: string;
  phone_verified?: boolean;
  phone_verify_check?: string;
  qs_id?: string;
  roof_age?: string;
  send_solar_guide?: boolean;
  sun?: string;
  utility_provider?: string;
  vid: string;
  zip?: string;
};

export type Contact = Pick<Visitor, 'address' | 'email' | 'first_name' | 'last_name' | 'message' | 'phone'>;

export type BatteryChoice = { count: number; days?: number };

export type PanelChoice = { count: number; perc: number };

export type SpecChoice = { batteryCount?: number; panelCount?: number };

export type VisitorCookie = Pick<
  Visitor,
  | 'address'
  | 'battery_option'
  | 'bill'
  | 'email_verified'
  | 'email_verify_check'
  | 'email'
  | 'first_name'
  | 'geolocation'
  | 'in_market'
  | 'intent'
  | 'isBot'
  | 'last_name'
  | 'locale'
  | 'message'
  | 'otp_verify_check'
  | 'panel_option'
  | 'partner_ids'
  | 'payment_type'
  | 'phone_verified'
  | 'phone_verify_check'
  | 'phone'
  | 'qs_id'
  | 'roof_age'
  | 'send_solar_guide'
  | 'utility_provider'
  | 'vid'
  | 'zip'
> & {
  meta_values: Pick<
    MetaValues,
    | '_fbc'
    | '_fbp'
    | 'affiliate_id'
    | 'channel'
    | 'embed_display_mode'
    | 'embed_in_service_area'
    | 'embed_origin'
    | 'embed_partner_id'
    | 'experiments'
    | 'fbclid'
    | 'gclid'
    | 'intent'
    | 'intent_tiles_order'
    | 'is_likely_bot'
    | 'landing_load_time'
    | 'landing_page'
    | 'msclkid'
    | 'prospective_partner_ids'
    | 'source'
    | 'url_domain'
    | 'utm_campaign'
    | 'utm_content'
    | 'utm_medium'
    | 'utm_source'
    | 'utm_term'
  >;
};

// Trim down the visitor object to keep the cookie small
// Exported for testing
export const toVisitorCookie = (visitor: Visitor | undefined): VisitorCookie | undefined => {
  if (!visitor) {
    return visitor;
  }

  const {
    _id,
    address,
    battery_option,
    bill,
    email_verified,
    email_verify_check,
    email,
    first_name,
    geolocation,
    in_market,
    intent,
    isBot,
    last_name,
    locale,
    message,
    meta_values = {},
    otp_verify_check,
    panel_option,
    partner_ids,
    payment_type,
    phone_verified,
    phone_verify_check,
    phone,
    send_solar_guide,
    qs_id,
    roof_age,
    utility_provider,
    vid,
    zip,
  } = visitor;

  const {
    _fbc,
    _fbp,
    affiliate_id,
    channel,
    embed_display_mode,
    embed_in_service_area,
    embed_origin,
    embed_partner_id,
    experiments,
    fbclid,
    gclid,
    intent_tiles_order,
    is_likely_bot,
    landing_load_time,
    landing_page,
    msclkid,
    prospective_partner_ids,
    source,
    url_domain,
    utm_campaign,
    utm_content,
    utm_medium,
    utm_source,
    utm_term,
  } = meta_values;

  const metaValues = stripNulls({
    _fbc,
    _fbp,
    affiliate_id,
    channel,
    embed_display_mode,
    embed_in_service_area,
    embed_origin,
    embed_partner_id,
    experiments,
    fbclid,
    gclid,
    intent,
    intent_tiles_order,
    is_likely_bot,
    landing_load_time,
    landing_page,
    msclkid,
    prospective_partner_ids,
    source,
    url_domain,
    utm_campaign,
    utm_content,
    utm_medium,
    utm_source,
    utm_term,
  });

  return stripNulls<VisitorCookie>({
    address,
    battery_option,
    bill,
    email,
    email_verified,
    email_verify_check,
    first_name,
    geolocation,
    in_market,
    intent,
    isBot,
    last_name,
    locale,
    message,
    meta_values: Object.keys(metaValues).length ? metaValues : undefined,
    otp_verify_check,
    panel_option,
    partner_ids,
    payment_type,
    phone,
    phone_verified,
    phone_verify_check,
    qs_id,
    roof_age,
    send_solar_guide,
    utility_provider,
    vid: vid ?? _id,
    zip,
  });
};

export const diffVisitor = (visitor: Visitor, candidate: Partial<Visitor>): Partial<Visitor> | undefined => {
  const updates: Partial<Visitor> = Object.fromEntries(
    Object.entries(stripNulls(candidate)).filter(([key, val]) => !isEqual(val, visitor[key as keyof Visitor]))
  );

  if (Object.keys(updates).length > 0) {
    return updates;
  }
};

export type Session = { expires: Date; sessionId?: string | null };

export interface LDUser extends LDSingleKindContext {
  affiliate_id?: AffiliateId;
  affiliate_name?: string;
  funnel_type?: FunnelType;
  isTester?: boolean;
  key: string;
  kind: 'user';
  utm_source?: string;
}

export type EmbedDisplayMode = 'overlay';

export type MetaValues = {
  _fbc?: string; // Meta Ads (Facebook) click ID
  _fbp?: string; // Meta Ads (Facebook) browser ID
  affiliate_id?: AffiliateId;
  anonID?: string | null;
  channel?: Channel; // derived acquisition channel (CONS-2478)
  deviceBrowser?: string;
  deviceOs?: string;
  deviceType?: string;
  deviceUserAgent?: string;
  embed_display_mode?: EmbedDisplayMode;
  embed_in_service_area?: boolean;
  embed_origin?: string;
  embed_partner_id?: string;
  enzyCloser?: string;
  experiments?: Experiment[];
  fbclid?: string; // Meta Ads (Facebook) click ID (URL param)
  flags?: Partial<LDFlagsDashCase>;
  ga_client_id?: string; // GA4 client id from the `_ga` cookie; maps to GA4 client_id (MKT-2674)
  gclid?: string; // Google Ads click ID
  intent?: string;
  intent_tiles_order?: string[];
  ip_address?: string;
  is_likely_bot?: boolean; // Vercel BotID verdict; unset until a check succeeds
  landing_affiliate_id?: AffiliateId;
  landing_load_time?: number;
  landing_page?: string;
  landing_url?: string;
  loc_interest_ms?: string;
  loc_physical_ms?: string;
  msclkid?: string; // Microsoft Ads (Bing) click ID
  prospective_partner_ids?: string[];
  raw_url?: string;
  referrer?: string;
  source?: FunnelType;
  url_domain?: string;
  url_path?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_medium?: string;
  utm_source?: string;
  utm_term?: string;
  zip_from_ip?: string;
  // eslint-disable-next-line @typescript-eslint/member-ordering
  [key: string]: unknown;
};

export const savedVisitorSchema = z.object({
  data_found: z.boolean().optional(),
  email_verified: z.boolean().optional(),
  email_verify_check: z.string().optional(),
  geolocation: geolocationSchema.optional(),
  in_market: z.boolean().optional(),
  meta_values: z.object({ embed_in_service_area: z.boolean().optional() }).optional(),
  otp_skipped_at: z.string().optional(),
  otp_verify_check: z.boolean().optional(),
  partner_ids: z.string().array().optional(),
  phone_verified: z.boolean().optional(),
  phone_verify_check: z.string().optional(),
  qs_id: z.string().optional(),
});

export type SavedVisitor = z.infer<typeof savedVisitorSchema>;
